// 재제작 분석 — 2026-08-09 개편 (기공비 분석과 같은 화면 문법)
// 기획서: docs/기획서_치과분석_기공비_재제작_2026-08.md §5-2
//
// 원천: workOrders(웹 의뢰서)의 isRemake / remakeType / remakeCause 뿐. 신규 컬렉션·서버 함수 없음.
// 원칙 3가지
//   ① 순위표가 아니라 원인 찾기 화면 — 표본이 적은 상대는 '표본 적음'으로 표시하고 비율을 강조하지 않는다
//   ② 사유는 표준 코드(remakeCause) 기준. 옛 데이터는 '미분류' — 자유 입력을 임의로 분류하지 않는다
//   ③ 커버리지를 항상 밝힌다 — 전화·수기 의뢰는 웹 의뢰서가 없어 여기에 잡히지 않는다
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { RotateCcw } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { remakeCauseLabel } from '../utils/remakeCauses';
import { cleanPatientName } from '../utils/patientName';
import { koreanDisplayName } from '../utils/statementItems';

const toDate = (ts) => {
    if (!ts) return null;
    if (ts.toDate) return ts.toDate();
    if (ts instanceof Date) return ts;
    const d = new Date(ts);
    return isNaN(d) ? null : d;
};
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// 사유 집계 기준: 표준 사유 코드 우선, 없으면 '미분류'
const causeOf = (o) => (o.remakeCause ? remakeCauseLabel(o.remakeCause) : '미분류');
// 재제작 = 리메이크만. '수정(modify)'은 다시 만든 게 아니라 손본 것이라 제외한다
// 2026-09-04: '수리(remakeKind repair)'도 새로 만든 게 아니라 분리 집계한다. remakeKind 없는 구데이터 = 재제작.
const isRepairOrder = (o) => o.isRemake === true && o.remakeType !== 'modify' && o.remakeKind === 'repair';
const isRemakeOrder = (o) => o.isRemake === true && o.remakeType !== 'modify' && o.remakeKind !== 'repair';

const maskPatient = (raw) => {
    const s = (cleanPatientName(raw)?.name || String(raw || '')).trim();
    if (!s) return '';
    if (s.length === 1) return '○';
    if (s.length === 2) return s[0] + '○';
    return s[0] + '○'.repeat(s.length - 2) + s[s.length - 1];
};

// 치식(FDI) → 부위. 11~13/21~23/31~33/41~43 전치, x4·x5 소구치, x6~x8 대구치
const toothZone = (n) => {
    const t = parseInt(String(n).replace(/[^\d]/g, ''), 10);
    if (!t || t < 11 || t > 48) return null;
    const pos = t % 10;
    if (pos >= 1 && pos <= 3) return '앞니(전치)';
    if (pos === 4 || pos === 5) return '작은어금니';
    if (pos >= 6 && pos <= 8) return '큰어금니';
    return null;
};

// 의뢰서 1건의 치식 모으기 (2026-08-09 실데이터 확인)
//  운영 데이터는 teethDetails·selectedTeeth가 거의 없고 items[].toothNumber / prosthesisGroups[].teeth에 들어 있다.
//  한 곳만 보면 치아 수가 통째로 0이 된다(실측: 아이반랩 870건 전부 0) — 네 군데를 모두 훑고 중복은 제거한다.
const teethOf = (o) => {
    const set = new Set();
    const add = (t) => {
        const n = parseInt(String(t).replace(/[^\d]/g, ''), 10);
        if (n >= 11 && n <= 48) set.add(n);
    };
    (o.teethDetails || []).forEach(t => add(t.tooth ?? t.toothNumber));
    (o.selectedTeeth || []).forEach(add);
    (o.items || []).forEach(i => add(i.toothNumber ?? i.tooth));
    [...(o.prosthesisGroups || []), ...(o.bridgeGroups || []), ...(o.ponticGroups || []), ...(o.implantGroups || [])]
        .forEach(g => {
            (g.teeth || []).forEach(add);
            (g.abutments || []).forEach(add);
            (g.pontics || []).forEach(add);
        });
    return [...set];
};

// 의뢰서 1건에서 보철 종류·재료·치식 뽑기 (있는 것만 — 없으면 조용히 건너뛴다)
const detailsOf = (o) => {
    const pros = new Set(), mats = new Set(), zones = new Set();
    const push = (p, m) => {
        const kr = koreanDisplayName(p || '', m || '');
        if (kr) pros.add(kr);
        if (m) mats.add(m);
    };
    (o.teethDetails || []).forEach(t => push(t.prosthesis, t.material));
    (o.prosthesisGroups || []).forEach(g => push(g.prosthesis, g.material));
    (o.items || []).forEach(i => push(i.prosthesis, i.material));
    teethOf(o).forEach(t => { const z = toothZone(t); if (z) zones.add(z); });
    return { pros: [...pros], mats: [...mats], zones: [...zones] };
};

function RemakeAnalysis({ user }) {
    const [loading, setLoading] = useState(true);
    const [allOrders, setAllOrders] = useState([]);
    const [mode, setMode] = useState('month');          // 'month' = 특정 월 / 'range' = 최근 N개월 (대표 요청 2026-08-09)
    const [months, setMonths] = useState(6);
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [month, setMonth] = useState(() => new Date().getMonth() + 1);
    const [error, setError] = useState('');

    const uid = user?.companyId || user?.uid;

    const fetchOrders = useCallback(async () => {
        if (!uid) return;
        setLoading(true);
        setError('');
        try {
            const ref = collection(db, 'workOrders');
            const [s1, s2] = await Promise.all([
                getDocs(query(ref, where('fromUserId', '==', uid))),
                getDocs(query(ref, where('toUserId', '==', uid))),
            ]);
            const map = new Map();
            s1.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
            s2.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
            setAllOrders([...map.values()]);
        } catch (e) {
            console.error('[재제작 분석] 조회 실패:', e);
            setError('의뢰서를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            setLoading(false);
        }
    }, [uid]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    // 조회 구간 [since, until) — 월 선택이면 그 달 하루치까지, 기간이면 최근 N개월
    const { since, until, periodLabel } = useMemo(() => {
        if (mode === 'month') {
            const a = new Date(year, month - 1, 1, 0, 0, 0, 0);
            const b = new Date(year, month, 1, 0, 0, 0, 0);
            return { since: a, until: b, periodLabel: `${year}년 ${month}월` };
        }
        const a = new Date();
        a.setMonth(a.getMonth() - (months - 1));
        a.setDate(1); a.setHours(0, 0, 0, 0);
        return { since: a, until: null, periodLabel: `최근 ${months}개월` };
    }, [mode, year, month, months]);

    // 에이전트 자동수집분(agent_auto)은 실제 의뢰서가 아니라 파일 수집 복제본이라 분모에서 뺀다
    const orders = useMemo(() => allOrders.filter(o => {
        if (o.source === 'agent_auto') return false;
        const d = toDate(o.createdAt);
        if (!d || d < since) return false;
        return until ? d < until : true;
    }), [allOrders, since, until]);

    // 같은 기간의 '수정'(modify) — 다시 만든 건 아니지만 손이 두 번 간 건이라 함께 센다
    const modifies = useMemo(() => orders.filter(o => o.isRemake === true && o.remakeType === 'modify'), [orders]);

    const remakes = useMemo(() => orders.filter(isRemakeOrder), [orders]);
    // 같은 기간의 '수리' — 기존 보철을 고친 건. 재제작률 분자에서 빼고 따로 센다 (2026-09-04)
    const repairs = useMemo(() => orders.filter(isRepairOrder), [orders]);

    const partnerOf = useCallback((o) => (
        o.fromUserId === uid ? (o.toUserName || '이름 없음') : (o.fromUserName || '이름 없음')
    ), [uid]);

    // ── 요약 ──
    const summary = useMemo(() => {
        const total = orders.length, r = remakes.length;
        const now = new Date();
        const thisKey = monthKey(now);
        const thisMonth = remakes.filter(o => { const d = toDate(o.createdAt); return d && monthKey(d) === thisKey; }).length;
        const teeth = remakes.reduce((a, o) => a + teethOf(o).length, 0);
        const causes = {};
        remakes.forEach(o => { const c = causeOf(o); causes[c] = (causes[c] || 0) + 1; });
        const top = Object.entries(causes).filter(([k]) => k !== '미분류').sort((a, b) => b[1] - a[1])[0];
        const coded = remakes.filter(o => !!o.remakeCause).length;
        return {
            total, remakes: r, thisMonth, teeth,
            rate: total ? (r / total) * 100 : 0,
            topCause: top ? top[0] : '-',
            topCauseCount: top ? top[1] : 0,
            codedPct: r ? (coded / r) * 100 : 0,
        };
    }, [orders, remakes]);

    // ── 월별 추이 ──
    const trend = useMemo(() => {
        const m = new Map();
        // 월 선택 모드면 그 달을 오른쪽 끝에 두고 6개월, 기간 모드면 최근 N개월
        const span = mode === 'month' ? 6 : months;
        const anchor = mode === 'month' ? new Date(year, month - 1, 1) : new Date();
        for (let i = span - 1; i >= 0; i--) {
            const d = new Date(anchor.getFullYear(), anchor.getMonth() - i, 1);
            m.set(monthKey(d), { total: 0, remakes: 0 });
        }
        // 추이는 선택 구간이 아니라 전체 데이터에서 그린다 (한 달만 보고 있어도 흐름은 보여야 한다)
        allOrders.forEach(o => {
            if (o.source === 'agent_auto') return;
            const d = toDate(o.createdAt);
            if (!d) return;
            const k = monthKey(d);
            if (!m.has(k)) return;
            const cur = m.get(k);
            cur.total += 1;
            if (isRemakeOrder(o)) cur.remakes += 1;
        });
        const arr = [...m.entries()].map(([k, v]) => ({
            key: k, label: `${Number(k.slice(5))}월`,
            ...v, rate: v.total ? (v.remakes / v.total) * 100 : 0,
        }));
        return { arr, max: Math.max(...arr.map(a => a.remakes), 1) };
    }, [allOrders, mode, months, year, month]);

    // ── 원인별 ──
    const causes = useMemo(() => {
        const m = new Map();
        remakes.forEach(o => {
            const c = causeOf(o);
            m.set(c, (m.get(c) || 0) + 1);
        });
        const arr = [...m.entries()]
            .map(([name, count]) => ({ name, count, pct: remakes.length ? (count / remakes.length) * 100 : 0 }))
            .sort((a, b) => b.count - a.count);
        return { arr, max: Math.max(...arr.map(a => a.count), 1) };
    }, [remakes]);

    // ── 상대 업체별 ──
    const partners = useMemo(() => {
        const m = new Map();
        orders.forEach(o => {
            const p = partnerOf(o);
            const cur = m.get(p) || { name: p, total: 0, remakes: 0, repairs: 0, causes: {} };
            cur.total += 1;
            if (isRepairOrder(o)) cur.repairs += 1;   // 수리는 비율 분자에 넣지 않고 열로만 보여준다
            if (isRemakeOrder(o)) {
                cur.remakes += 1;
                const c = causeOf(o);
                cur.causes[c] = (cur.causes[c] || 0) + 1;
            }
            m.set(p, cur);
        });
        return [...m.values()]
            .map(x => ({
                ...x,
                rate: x.total ? (x.remakes / x.total) * 100 : 0,
                thin: x.total < 10,   // 표본이 적으면 비율을 크게 읽지 않는다
                topCause: Object.entries(x.causes).sort((a, b) => b[1] - a[1])[0]?.[0] || '-',
            }))
            .sort((a, b) => b.remakes - a.remakes);
    }, [orders, partnerOf]);

    // ── 갈래별(보철·재료·부위) 재제작률 ──
    const breakdown = useMemo(() => {
        const mk = () => new Map();
        const pros = mk(), mats = mk(), zones = mk();
        const add = (map, key, remake) => {
            const cur = map.get(key) || { name: key, total: 0, remakes: 0 };
            cur.total += 1;
            if (remake) cur.remakes += 1;
            map.set(key, cur);
        };
        orders.forEach(o => {
            const r = isRemakeOrder(o);
            const d = detailsOf(o);
            d.pros.forEach(p => add(pros, p, r));
            d.mats.forEach(m => add(mats, m, r));
            d.zones.forEach(z => add(zones, z, r));
        });
        const shape = (map) => [...map.values()]
            .filter(x => x.total >= 3)               // 3건 미만은 비율이 요동쳐 의미 없다
            .map(x => ({ ...x, rate: (x.remakes / x.total) * 100 }))
            .sort((a, b) => b.rate - a.rate)
            .slice(0, 8);
        return { pros: shape(pros), mats: shape(mats), zones: shape(zones) };
    }, [orders]);

    // ── 케이스 크기별 (2026-08-09) — 한 건에 치아가 몇 개였나. 큰 케이스가 더 자주 실패하는지 본다 ──
    const bySize = useMemo(() => {
        const bucket = (n) => (n <= 1 ? '1개(단일)' : n <= 2 ? '2개' : n <= 4 ? '3~4개' : '5개 이상');
        const m = new Map();
        orders.forEach(o => {
            const n = teethOf(o).length;
            if (!n) return;
            const k = bucket(n);
            const cur = m.get(k) || { name: k, total: 0, remakes: 0 };
            cur.total += 1;
            if (isRemakeOrder(o)) cur.remakes += 1;
            m.set(k, cur);
        });
        const order = ['1개(단일)', '2개', '3~4개', '5개 이상'];
        return [...m.values()]
            .filter(x => x.total >= 3)
            .map(x => ({ ...x, rate: (x.remakes / x.total) * 100 }))
            .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
    }, [orders]);

    // ── 스캔 유무별 (2026-08-09) — 구강스캔이 붙은 의뢰가 실제로 덜 실패하는지 ──
    const byScan = useMemo(() => {
        const m = { scan: { name: '스캔 있음', total: 0, remakes: 0 }, none: { name: '스캔 없음', total: 0, remakes: 0 } };
        orders.forEach(o => {
            const hasScan = !!o.scanId || (Array.isArray(o.digitalFiles) && o.digitalFiles.length > 0);
            const k = hasScan ? 'scan' : 'none';
            m[k].total += 1;
            if (isRemakeOrder(o)) m[k].remakes += 1;
        });
        return Object.values(m)
            .filter(x => x.total >= 3)
            .map(x => ({ ...x, rate: (x.remakes / x.total) * 100 }));
    }, [orders]);

    // ── 사유 원문 목록 (2026-08-09) — 분포보다 현장 문장이 원인을 더 잘 말해준다 ──
    const recentNotes = useMemo(() => (
        remakes
            .filter(o => (o.remakeReason || '').trim())
            .map(o => ({
                id: o.id,
                date: toDate(o.createdAt),
                patient: maskPatient(o.patientName),
                partner: partnerOf(o),
                cause: causeOf(o),
                reason: (o.remakeReason || '').trim(),
            }))
            .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))
            .slice(0, 12)
    ), [remakes, partnerOf]);

    // ── 원 의뢰서와 연결된 건 (2026-08-09 대표 확정) ──
    // remakeOfOrderId가 있는 재제작만 계산한다 — 환자 이름으로 짐작하지 않는다(동명이인·다른 치아 오인).
    const linked = useMemo(() => {
        const byId = new Map(allOrders.map(o => [o.id, o]));
        const rows = remakes.map(o => {
            if (!o.remakeOfOrderId) return null;
            const origin = byId.get(o.remakeOfOrderId);
            const od = toDate(o.remakeOfOrderDate) || (origin ? toDate(origin.createdAt) : null);
            const rd = toDate(o.createdAt);
            if (!od || !rd) return null;
            const days = Math.round((rd - od) / (24 * 3600 * 1000));
            if (days < 0) return null;
            return { id: o.id, days, originId: o.remakeOfOrderId, cause: causeOf(o), partner: partnerOf(o) };
        }).filter(Boolean);

        // 같은 원 의뢰서를 몇 번 다시 만들었나
        const counts = new Map();
        rows.forEach(r => counts.set(r.originId, (counts.get(r.originId) || 0) + 1));
        const repeated = [...counts.values()].filter(n => n >= 2).length;

        const buckets = [
            { name: '7일 이내', min: 0, max: 7 },
            { name: '8~30일', min: 8, max: 30 },
            { name: '31~90일', min: 31, max: 90 },
            { name: '90일 초과', min: 91, max: Infinity },
        ].map(b => ({ ...b, count: rows.filter(r => r.days >= b.min && r.days <= b.max).length }));

        const avg = rows.length ? Math.round(rows.reduce((a, r) => a + r.days, 0) / rows.length) : null;
        const median = rows.length
            ? [...rows].sort((a, b) => a.days - b.days)[Math.floor(rows.length / 2)].days
            : null;
        return {
            rows, avg, median, repeated,
            buckets: buckets.filter(b => b.count > 0),
            linkedPct: remakes.length ? (rows.length / remakes.length) * 100 : 0,
            max: Math.max(...buckets.map(b => b.count), 1),
        };
    }, [remakes, allOrders, partnerOf]);

    // ── 반복 재제작 환자 ──
    const repeats = useMemo(() => {
        const m = new Map();
        remakes.forEach(o => {
            const key = (cleanPatientName(o.patientName)?.name || o.patientName || '').trim();
            if (!key) return;
            const cur = m.get(key) || { key, count: 0, partners: new Set(), last: null };
            cur.count += 1;
            cur.partners.add(partnerOf(o));
            const d = toDate(o.createdAt);
            if (d && (!cur.last || d > cur.last)) cur.last = d;
            m.set(key, cur);
        });
        return [...m.values()].filter(x => x.count >= 2).sort((a, b) => b.count - a.count).slice(0, 6);
    }, [remakes, partnerOf]);

    const prevMonth = () => { if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1); };
    const nextMonth = () => { if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1); };

    const rateColor = (v) => (v <= 3 ? '#006300' : v <= 6 ? '#8a6100' : '#d03b3b');

    if (loading) return (
        <div style={S.page}>
            <PageHeader icon={RotateCcw} title="재제작 분석" />
            <div style={S.empty}>불러오는 중…</div>
        </div>
    );

    return (
        <div style={S.page}>
            <PageHeader icon={RotateCcw} title="재제작 분석" />

            <div style={S.topbar}>
                <div style={S.topText}>웹 의뢰서 기준 · {periodLabel} · 재제작만 셉니다('수정'은 따로 표시)</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => setMode('month')} style={{ ...S.chip, ...(mode === 'month' ? S.chipOn : {}) }}>월별</button>
                    {mode === 'month' ? (
                        <>
                            <button onClick={prevMonth} style={S.navBtn}>‹</button>
                            <span style={S.monthLabel}>{year}.{String(month).padStart(2, '0')}</span>
                            <button onClick={nextMonth} style={S.navBtn}>›</button>
                        </>
                    ) : null}
                    {[3, 6, 12].map(m => (
                        <button key={m} onClick={() => { setMode('range'); setMonths(m); }}
                            style={{ ...S.chip, ...(mode === 'range' && months === m ? S.chipOn : {}) }}>{m}개월</button>
                    ))}
                </div>
            </div>

            {error && <div style={S.error}>{error}</div>}

            {orders.length === 0 ? (
                <div style={S.empty}>이 기간에 웹으로 주고받은 의뢰서가 없습니다.</div>
            ) : (
                <>
                    {/* 1. 타일 */}
                    <div style={S.tiles}>
                        <div style={S.tile}>
                            <div style={S.tk}>이번 달 재제작</div>
                            <div style={S.tv}>{summary.thisMonth}<small style={S.tu}>건</small></div>
                            <div style={S.td}>기간 전체 {summary.remakes}건</div>
                        </div>
                        <div style={S.tile}>
                            <div style={S.tk}>재제작률</div>
                            <div style={{ ...S.tv, color: rateColor(summary.rate) }}>{summary.rate.toFixed(1)}<small style={S.tu}>%</small></div>
                            <div style={S.td}>의뢰 {summary.total}건 중 {summary.remakes}건</div>
                        </div>
                        <div style={S.tile}>
                            <div style={S.tk}>다시 만든 치아</div>
                            <div style={S.tv}>{summary.teeth}<small style={S.tu}>개</small></div>
                            <div style={S.td}>같은 기간 '수리' {repairs.length}건 · '수정' {modifies.length}건</div>
                        </div>
                        <div style={S.tile}>
                            <div style={S.tk}>가장 잦은 원인</div>
                            <div style={{ ...S.tv, fontSize: 19 }}>{summary.topCause}</div>
                            <div style={S.td}>{summary.topCauseCount ? `${summary.topCauseCount}건` : '사유 기록이 아직 없습니다'}</div>
                        </div>
                    </div>

                    {/* 2. 월별 추이 */}
                    <div style={S.card}>
                        <h3 style={S.h3}>월별 재제작</h3>
                        <p style={S.cap}>막대는 건수, 아래 숫자는 그달의 재제작률입니다.</p>
                        <div style={S.trend}>
                            {trend.arr.map(t => (
                                <div key={t.key} style={S.tcol}>
                                    <div style={S.tval}>{t.remakes || '-'}</div>
                                    <div style={{
                                        ...S.tbar,
                                        height: `${Math.max(4, (t.remakes / trend.max) * 100)}%`,
                                        background: t.rate > 6 ? '#d03b3b' : '#86b6ef',
                                    }} />
                                    <div style={S.tlab}>{t.label}</div>
                                    <div style={{ ...S.tlab, color: rateColor(t.rate), fontWeight: 700 }}>
                                        {t.total ? `${t.rate.toFixed(0)}%` : '-'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 3. 원인별 */}
                    <div style={S.card}>
                        <h3 style={S.h3}>원인별 분포</h3>
                        <p style={S.cap}>
                            의뢰서에 고른 사유 기준입니다. 사유를 안 고른 건은 <b>미분류</b>로 둡니다 — 옛 기록을 임의로 분류하지 않습니다.
                            {summary.codedPct < 100 && ` (사유가 기록된 비율 ${summary.codedPct.toFixed(0)}%)`}
                        </p>
                        {causes.arr.length === 0 ? (
                            <div style={S.sub}>이 기간에 재제작이 없습니다.</div>
                        ) : causes.arr.map(c => (
                            <div key={c.name} style={S.brow}>
                                <div style={S.blab}>{c.name}</div>
                                <div style={S.btrack}>
                                    <div style={{
                                        ...S.bfill,
                                        width: `${Math.max(4, (c.count / causes.max) * 100)}%`,
                                        background: c.name === '미분류' ? '#c9d3e0' : '#2a78d6',
                                    }}>
                                        <span style={S.bamt}>{c.count}건</span>
                                    </div>
                                </div>
                                <div style={S.brt}>{c.pct.toFixed(0)}%</div>
                            </div>
                        ))}
                    </div>

                    {/* 4. 상대 업체별 */}
                    <div style={S.card}>
                        <h3 style={S.h3}>거래처별</h3>
                        <p style={S.cap}>
                            순위표가 아니라 어디서 몰리는지 보는 표입니다. 의뢰 10건이 안 되는 곳은 <b>표본 적음</b>으로 표시했습니다 — 비율을 크게 읽지 마세요.
                        </p>
                        <table style={S.table}>
                            <thead>
                                <tr>
                                    <th style={S.th}>거래처</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>의뢰</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>재제작</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>수리</th>
                                    <th style={{ ...S.th, textAlign: 'right' }}>비율</th>
                                    <th style={S.th}>가장 잦은 원인</th>
                                </tr>
                            </thead>
                            <tbody>
                                {partners.slice(0, 10).map(p => (
                                    <tr key={p.name}>
                                        <td style={{ ...S.td2, fontWeight: 700, color: '#0f172a' }}>
                                            {p.name}{p.thin && <span style={S.thin}>표본 적음</span>}
                                        </td>
                                        <td style={{ ...S.td2, textAlign: 'right' }}>{p.total}</td>
                                        <td style={{ ...S.td2, textAlign: 'right' }}>{p.remakes}</td>
                                        <td style={{ ...S.td2, textAlign: 'right', color: p.repairs ? '#0e7490' : '#94a3b8' }}>{p.repairs}</td>
                                        <td style={{ ...S.td2, textAlign: 'right', fontWeight: 800, color: p.thin ? '#94a3b8' : rateColor(p.rate) }}>
                                            {p.rate.toFixed(1)}%
                                        </td>
                                        <td style={S.td2}>{p.remakes ? p.topCause : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* 5. 갈래별 */}
                    <div style={S.card}>
                        <h3 style={S.h3}>어디서 몰리나</h3>
                        <p style={S.cap}>보철 종류·재료·부위별 재제작률입니다. 3건 이상 있는 항목만 나옵니다(적으면 비율이 요동칩니다).</p>
                        <div style={S.three}>
                            {[
                                { title: '보철 종류', list: breakdown.pros },
                                { title: '재료', list: breakdown.mats },
                                { title: '부위', list: breakdown.zones },
                            ].map(col => (
                                <div key={col.title} style={S.col}>
                                    <div style={S.colTitle}>{col.title}</div>
                                    {col.list.length === 0 ? (
                                        <div style={S.sub}>자료가 아직 적습니다.</div>
                                    ) : col.list.map(x => (
                                        <div key={x.name} style={S.crow2}>
                                            <span style={S.cname}>{x.name}</span>
                                            <span style={{ ...S.crate, color: rateColor(x.rate) }}>{x.rate.toFixed(0)}%</span>
                                            <span style={S.ccount}>{x.remakes}/{x.total}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 5-1. 다시 만들기까지 걸린 시간 (원 의뢰서를 연결한 건만) */}
                    <div style={S.card}>
                        <h3 style={S.h3}>처음 만든 뒤 얼마 만에 다시 만들었나</h3>
                        <p style={S.cap}>
                            의뢰서에서 <b>원 의뢰서를 지목한 건</b>만 셉니다 — 환자 이름으로 짐작하면 동명이인·다른 치아가 섞여 숫자가 틀어집니다.
                            {linked.rows.length > 0 && ` (재제작 ${remakes.length}건 중 ${linked.rows.length}건 연결됨 · ${linked.linkedPct.toFixed(0)}%)`}
                        </p>
                        {linked.rows.length === 0 ? (
                            <div style={S.sub}>
                                아직 원 의뢰서를 지목한 건이 없습니다. 앞으로 리메이크·수정을 고를 때 원 의뢰서를 함께 고르면 여기가 채워집니다.
                            </div>
                        ) : (
                            <>
                                <div style={S.linkTiles}>
                                    <div style={S.linkTile}><div style={S.tk}>평균</div><div style={S.linkV}>{linked.avg}<small style={S.tu}>일</small></div></div>
                                    <div style={S.linkTile}><div style={S.tk}>중간값</div><div style={S.linkV}>{linked.median}<small style={S.tu}>일</small></div></div>
                                    <div style={S.linkTile}><div style={S.tk}>두 번 이상 다시 만든 케이스</div><div style={S.linkV}>{linked.repeated}<small style={S.tu}>건</small></div></div>
                                </div>
                                <div style={{ marginTop: 12 }}>
                                    {linked.buckets.map(b => (
                                        <div key={b.name} style={S.brow}>
                                            <div style={S.blab}>{b.name}</div>
                                            <div style={S.btrack}>
                                                <div style={{ ...S.bfill, width: `${Math.max(4, (b.count / linked.max) * 100)}%`, background: b.name === '7일 이내' ? '#d03b3b' : '#2a78d6' }}>
                                                    <span style={S.bamt}>{b.count}건</span>
                                                </div>
                                            </div>
                                            <div style={S.brt}>{((b.count / linked.rows.length) * 100).toFixed(0)}%</div>
                                        </div>
                                    ))}
                                </div>
                                <div style={S.sub}>7일 이내가 많으면 만드는 과정, 몇 달 뒤가 많으면 사용 중 문제일 가능성이 큽니다.</div>
                            </>
                        )}
                    </div>

                    {/* 5-2. 케이스 크기 · 스캔 유무 (2026-08-09 추가) */}
                    {(bySize.length > 0 || byScan.length > 0) && (
                        <div style={S.card}>
                            <h3 style={S.h3}>케이스 크기 · 스캔 유무</h3>
                            <p style={S.cap}>
                                한 건에 치아가 많을수록, 또는 스캔 없이 맡길수록 다시 만드는 일이 잦은지 봅니다. 3건 이상인 항목만 나옵니다.
                            </p>
                            <div style={S.three}>
                                <div style={S.col}>
                                    <div style={S.colTitle}>케이스 크기</div>
                                    {bySize.length === 0 ? <div style={S.sub}>자료가 아직 적습니다.</div> : bySize.map(x => (
                                        <div key={x.name} style={S.crow2}>
                                            <span style={S.cname}>{x.name}</span>
                                            <span style={{ ...S.crate, color: rateColor(x.rate) }}>{x.rate.toFixed(0)}%</span>
                                            <span style={S.ccount}>{x.remakes}/{x.total}</span>
                                        </div>
                                    ))}
                                </div>
                                <div style={S.col}>
                                    <div style={S.colTitle}>구강스캔 유무</div>
                                    {byScan.length === 0 ? <div style={S.sub}>자료가 아직 적습니다.</div> : byScan.map(x => (
                                        <div key={x.name} style={S.crow2}>
                                            <span style={S.cname}>{x.name}</span>
                                            <span style={{ ...S.crate, color: rateColor(x.rate) }}>{x.rate.toFixed(0)}%</span>
                                            <span style={S.ccount}>{x.remakes}/{x.total}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 5-3. 사유 원문 — 숫자보다 문장이 원인을 더 잘 말해준다 */}
                    {recentNotes.length > 0 && (
                        <div style={S.card}>
                            <h3 style={S.h3}>적어 보낸 사유 (최근 {recentNotes.length}건)</h3>
                            <p style={S.cap}>분포로는 안 보이는 구체적인 상황이 여기 있습니다. 환자 이름은 가려서 표시합니다.</p>
                            {recentNotes.map(n => (
                                <div key={n.id} style={S.noteRow}>
                                    <div style={S.noteHead}>
                                        <span style={S.noteDate}>{n.date ? `${n.date.getMonth() + 1}/${n.date.getDate()}` : ''}</span>
                                        <b>{n.patient || '환자 미상'}</b>
                                        <span style={S.noteMeta}>{n.partner}</span>
                                        <span style={S.noteCause}>{n.cause}</span>
                                    </div>
                                    <div style={S.noteText}>{n.reason}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 6. 반복 재제작 환자 */}
                    {repeats.length > 0 && (
                        <div style={S.card}>
                            <h3 style={S.h3}>두 번 이상 다시 만든 환자</h3>
                            <p style={S.cap}>이름은 가려서 표시합니다. 같은 환자가 반복된다면 케이스 자체를 다시 볼 신호입니다.</p>
                            {repeats.map(r => (
                                <div key={r.key} style={S.srow}>
                                    <span>{maskPatient(r.key)}</span>
                                    <b>{r.count}회 · {[...r.partners].join(', ')}</b>
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={S.note}>
                        <b>집계 범위</b> — 덴탈클러스터 <b>웹 의뢰서 {summary.total}건</b> 기준입니다.
                        전화·수기로 주고받은 건과 에이전트가 자동 수집한 파일은 여기에 잡히지 않습니다.
                    </div>
                    <div style={S.note}>
                        <b>사유에 대해</b> — 의뢰한 쪽이 적은 사유입니다. 덴탈클러스터는 어느 쪽 책임인지 판정하지 않습니다.
                        이 화면은 내부 참고용이며, 외부 공개나 광고에 쓰지 마세요.
                    </div>
                </>
            )}
        </div>
    );
}

const S = {
    page: { padding: 20, maxWidth: 1120, margin: '0 auto' },
    topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '4px 0 16px', flexWrap: 'wrap' },
    topText: { fontSize: 12.5, color: '#64748b' },
    chip: { padding: '6px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    chipOn: { background: '#6366f1', borderColor: '#6366f1', color: '#fff' },
    error: { background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '11px 13px', fontSize: 13, marginBottom: 12 },
    empty: { border: '1px dashed #e2e8f0', borderRadius: 9, padding: 26, textAlign: 'center', fontSize: 13, color: '#64748b', lineHeight: 1.8, background: '#fbfcfd' },

    tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 },
    tile: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' },
    tk: { fontSize: 11, color: '#64748b', fontWeight: 600 },
    tv: { fontSize: 25, fontWeight: 800, marginTop: 5, lineHeight: 1.15, letterSpacing: '-0.5px', color: '#0f172a' },
    tu: { fontSize: 13, fontWeight: 700, color: '#64748b', marginLeft: 2 },
    td: { fontSize: 11, marginTop: 6, fontWeight: 700, color: '#64748b' },

    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 14 },
    h3: { fontSize: 13, margin: '0 0 3px', fontWeight: 700, color: '#0f172a' },
    cap: { fontSize: 11.5, color: '#64748b', margin: '0 0 14px', lineHeight: 1.5 },
    sub: { fontSize: 12, color: '#64748b', lineHeight: 1.6 },

    trend: { display: 'flex', alignItems: 'flex-end', gap: 12, height: 165, padding: '0 4px' },
    tcol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, height: '100%', justifyContent: 'flex-end' },
    tbar: { width: '100%', maxWidth: 46, borderRadius: '4px 4px 0 0' },
    tval: { fontSize: 10.5, color: '#64748b' },
    tlab: { fontSize: 11, color: '#898781' },

    brow: { display: 'grid', gridTemplateColumns: '130px 1fr 50px', alignItems: 'center', gap: 10, marginBottom: 9 },
    blab: { fontSize: 12.5, color: '#334155', textAlign: 'right' },
    btrack: { height: 20, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' },
    bfill: { height: '100%', borderRadius: 4, display: 'flex', alignItems: 'center', paddingLeft: 8 },
    bamt: { fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' },
    brt: { fontSize: 12, color: '#64748b', textAlign: 'right' },

    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' },
    td2: { padding: '10px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9' },
    thin: { marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#8a6100', background: '#fff6e0', borderRadius: 5, padding: '2px 6px' },

    three: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 },
    col: { border: '1px solid #e2e8f0', borderRadius: 9, padding: '12px 13px', background: '#fbfcfd' },
    colTitle: { fontSize: 12.5, fontWeight: 700, color: '#0f172a', marginBottom: 10 },
    crow2: { display: 'grid', gridTemplateColumns: '1fr 52px 56px', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12.5 },
    cname: { color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    crate: { textAlign: 'right', fontWeight: 800 },
    ccount: { textAlign: 'right', color: '#94a3b8', fontSize: 11.5 },

    srow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, color: '#334155', padding: '8px 0', borderBottom: '1px solid #f1f5f9' },

    linkTiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 },
    linkTile: { border: '1px solid #e2e8f0', borderRadius: 9, padding: '11px 13px', background: '#fbfcfd' },
    linkV: { fontSize: 22, fontWeight: 800, color: '#0f172a', marginTop: 4, lineHeight: 1 },

    navBtn: { padding: '5px 10px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 14, fontWeight: 700, cursor: 'pointer', lineHeight: 1 },
    monthLabel: { fontSize: 13, fontWeight: 700, color: '#0f172a', minWidth: 62, textAlign: 'center' },

    noteRow: { padding: '9px 0', borderBottom: '1px solid #f1f5f9' },
    noteHead: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#0f172a', flexWrap: 'wrap' },
    noteDate: { color: '#94a3b8', fontSize: 11.5, minWidth: 34 },
    noteMeta: { color: '#64748b', fontSize: 11.5 },
    noteCause: { fontSize: 10.5, fontWeight: 700, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 5, padding: '2px 7px' },
    noteText: { fontSize: 12.5, color: '#334155', lineHeight: 1.6, marginTop: 4, whiteSpace: 'pre-line' },

    note: { marginTop: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', fontSize: 12.5, color: '#64748b', lineHeight: 1.75 },
};

export default RemakeAnalysis;
